import { useQuery } from '@tanstack/react-query';
import { sanityClient } from '../lib/sanity';

export interface SiteSettings {
  footer?: {
    socialMedia?: {
      facebook?: string;
      instagram?: string;
      twitter?: string;
      linkedin?: string;
      youtube?: string;
    };
    contactInfo?: {
      email?: string;
      phone?: string;
      phoneLink?: string;
    };
    tagline?: string;
  };
}

/**
 * Hook to fetch site-wide settings from Sanity CMS
 * Falls back to hardcoded defaults if no data is available
 */
export const useSiteSettings = () => {
  const dataset = import.meta.env.VITE_SANITY_DATASET || 'production';
  // Use shorter cache for non-production to see changes faster
  const staleTime = dataset === 'production' ? 1000 * 60 : 1000 * 10; // 1min prod, 10sec staging

  return useQuery<SiteSettings>({
    queryKey: ['siteSettings'],
    queryFn: async () => {
      const query = `*[_type == "siteSettings" && _id == "site-settings"][0] {
        footer {
          socialMedia {
            facebook,
            instagram,
            twitter,
            linkedin,
            youtube
          },
          contactInfo {
            email,
            phone,
            phoneLink
          },
          tagline
        }
      }`;

      const data = await sanityClient.fetch<SiteSettings | null>(query);
      return data || {};
    },
    staleTime,
    retry: 1, // Only retry once on failure
  });
};
